import React from "react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Mail, FileSpreadsheet, Search, UserCircle2, ClipboardList } from "lucide-react";

const SOURCES = [
  { id: "all", label: "Tous" },
  { id: "compte", label: "Comptes (auth)" },
  { id: "inscription", label: "Inscriptions" },
];

export default function MembresTab() {
  const [members, setMembers] = React.useState([]);
  const [search, setSearch] = React.useState("");
  const [source, setSource] = React.useState("all");
  const [province, setProvince] = React.useState("");
  const [school, setSchool] = React.useState("");
  const [annee, setAnnee] = React.useState("");
  const [reseau, setReseau] = React.useState("");
  const [sortKey, setSortKey] = React.useState("date_desc");
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(() => {
    setLoading(true);
    api.get("/admin/members")
      .then((r) => setMembers(r.data))
      .catch(() => { toast.error("Erreur de chargement"); })
      .finally(() => setLoading(false));
  }, []);
  React.useEffect(() => { load(); }, [load]);

  // Distinct facets for filter dropdowns
  const facets = React.useMemo(() => {
    const provs = new Set();
    const schools = new Set();
    const annees = new Set();
    const reseaux = new Set();
    members.forEach((m) => {
      if (m.province) provs.add(m.province);
      if (m.school_name) schools.add(m.school_name);
      if (m.annee) annees.add(m.annee);
      if (m.reseau) reseaux.add(m.reseau);
    });
    return {
      provinces: [...provs].sort(),
      schools: [...schools].sort(),
      annees: [...annees].sort(),
      reseaux: [...reseaux].sort(),
    };
  }, [members]);

  // Counts by school (for the "X élèves dans ton école" feature)
  const schoolCounts = React.useMemo(() => {
    const m = new Map();
    members.forEach((x) => {
      if (!x.school_name) return;
      m.set(x.school_name, (m.get(x.school_name) || 0) + 1);
    });
    return m;
  }, [members]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = members.filter((m) => {
      if (source !== "all" && m.source !== source) return false;
      if (province && m.province !== province) return false;
      if (school && m.school_name !== school) return false;
      if (annee && m.annee !== annee) return false;
      if (reseau && m.reseau !== reseau) return false;
      if (!q) return true;
      const blob = `${m.name || ""} ${m.last_name || ""} ${m.email || ""} ${m.school_name || ""} ${m.province || ""} ${m.reseau || ""}`.toLowerCase();
      return blob.includes(q);
    });
    const cmp = {
      date_desc: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
      date_asc: (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
      name_asc: (a, b) => (a.name || "").localeCompare(b.name || ""),
      name_desc: (a, b) => (b.name || "").localeCompare(a.name || ""),
      province_asc: (a, b) => (a.province || "zzz").localeCompare(b.province || "zzz"),
      school_asc: (a, b) => (a.school_name || "zzz").localeCompare(b.school_name || "zzz"),
      reseau_asc: (a, b) => (a.reseau || "zzz").localeCompare(b.reseau || "zzz"),
    }[sortKey] || (() => 0);
    return [...list].sort(cmp);
  }, [members, search, source, province, school, annee, reseau, sortKey]);

  const resetFilters = () => {
    setSearch(""); setSource("all"); setProvince(""); setSchool(""); setAnnee(""); setReseau(""); setSortKey("date_desc");
  };

  const del = async (m) => {
    if (!window.confirm(`Supprimer ${m.name || m.email} définitivement ?`)) return;
    try {
      const endpoint = m.source === "compte" ? `/admin/users/${m.id}` : `/admin/join-requests/${m.id}`;
      await api.delete(endpoint);
      toast.success("Supprimé");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const sendMail = (m) => {
    if (!m.email) return toast.error("Aucun email disponible");
    const subject = encodeURIComponent("Message du CEF — Élève ta voix");
    const body = encodeURIComponent(`Bonjour ${m.name || ""},\n\n`);
    window.location.href = `mailto:${m.email}?subject=${subject}&body=${body}`;
  };

  const exportCsv = () => {
    const token = localStorage.getItem("cef_admin_token");
    const url = `${API_BASE}/admin/exports/members.csv`;
    // Use fetch + blob to include Authorization header
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `cef_membres_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error("Export échoué"));
  };

  const counts = React.useMemo(() => ({
    all: members.length,
    compte: members.filter((m) => m.source === "compte").length,
    inscription: members.filter((m) => m.source === "inscription").length,
  }), [members]);

  return (
    <div data-testid="membres-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-display font-black text-2xl tracking-tighter uppercase">
          Membres <span className="text-neutral-500 text-base font-normal">({filtered.length}/{members.length})</span>
        </h2>
        <button onClick={exportCsv} className="neo-btn bg-cef-green text-sm py-2" data-testid="export-members-csv">
          <FileSpreadsheet className="w-4 h-4" /> Export CSV
        </button>
      </div>

      <div className="neo-card p-4 bg-white mb-5 grid gap-3" data-testid="members-toolbar">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
            <input
              className="neo-input pl-9 w-full"
              placeholder="Rechercher (nom, email, école, province…)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="search-members"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {SOURCES.map((s) => (
              <button
                key={s.id}
                onClick={() => setSource(s.id)}
                className={`chip ${source === s.id ? "bg-black text-white" : "bg-white"}`}
                data-testid={`filter-source-${s.id}`}
              >
                {s.label} <span className="ml-1 opacity-60">({counts[s.id]})</span>
              </button>
            ))}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
          <select className="neo-input" value={province} onChange={(e) => setProvince(e.target.value)} data-testid="filter-province">
            <option value="">Toutes provinces ({facets.provinces.length})</option>
            {facets.provinces.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select className="neo-input" value={reseau} onChange={(e) => setReseau(e.target.value)} data-testid="filter-reseau">
            <option value="">Tous réseaux ({facets.reseaux.length})</option>
            {facets.reseaux.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <select className="neo-input" value={school} onChange={(e) => setSchool(e.target.value)} data-testid="filter-school">
            <option value="">Toutes écoles ({facets.schools.length})</option>
            {facets.schools.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="neo-input" value={annee} onChange={(e) => setAnnee(e.target.value)} data-testid="filter-annee">
            <option value="">Toutes années ({facets.annees.length})</option>
            {facets.annees.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select className="neo-input" value={sortKey} onChange={(e) => setSortKey(e.target.value)} data-testid="sort-members">
            <option value="date_desc">Tri : Plus récents</option>
            <option value="date_asc">Tri : Plus anciens</option>
            <option value="name_asc">Tri : Nom (A→Z)</option>
            <option value="name_desc">Tri : Nom (Z→A)</option>
            <option value="province_asc">Tri : Province</option>
            <option value="school_asc">Tri : École</option>
            <option value="reseau_asc">Tri : Réseau</option>
          </select>
        </div>
        {(province || school || annee || reseau || search) && (
          <button onClick={resetFilters} className="text-xs font-bold underline self-start text-neutral-600" data-testid="reset-filters">
            Réinitialiser les filtres
          </button>
        )}
      </div>

      {loading && <div className="text-neutral-500">Chargement…</div>}

      <div className="grid gap-3" data-testid="members-list">
        {!loading && filtered.length === 0 && <div className="neo-card p-5 bg-white text-neutral-500">Aucun membre ne correspond à ces filtres.</div>}
        {filtered.map((m) => (
          <div key={`${m.source}-${m.id}`} className="neo-card p-4" data-testid={`member-${m.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-2 flex-wrap">
                  {m.source === "compte" ? (
                    <span className="chip bg-cef-blue text-white text-[10px] py-0.5"><UserCircle2 className="w-3 h-3 mr-1" /> compte</span>
                  ) : (
                    <span className="chip bg-cef-yellow text-[10px] py-0.5"><ClipboardList className="w-3 h-3 mr-1" /> inscription</span>
                  )}
                  <span className="font-bold">{m.name || "—"} {m.last_name || ""}</span>
                  {m.is_delegue && <span className="chip bg-cef-pink text-white text-[10px] py-0.5">délégué·e</span>}
                  {m.relais_local_optin && <span className="chip bg-cef-green text-[10px] py-0.5">relais</span>}
                </div>
                <div className="mt-1 text-sm text-neutral-700 grid sm:grid-cols-2 gap-x-3 gap-y-0.5">
                  <div>📧 {m.email || <span className="text-neutral-400">—</span>}</div>
                  {m.phone && <div>📞 {m.phone}</div>}
                  {m.school_name && (
                    <div>🏫 {m.school_name}
                      {schoolCounts.get(m.school_name) > 1 && (
                        <span className="ml-1 chip bg-cef-blue text-white text-[9px] py-0" data-testid={`school-count-${m.id}`}>+{schoolCounts.get(m.school_name) - 1} dans ton école</span>
                      )}
                    </div>
                  )}
                  {m.school_commune && <div>📍 {m.school_commune}{m.province ? ` · ${m.province}` : ""}</div>}
                  {m.reseau && <div data-testid={`member-reseau-${m.id}`}>🏛️ {m.reseau}</div>}
                  {(m.type_enseignement || m.annee) && <div>🎓 {m.type_enseignement || "—"} {m.annee ? `· ${m.annee}` : ""}</div>}
                  {m.age && <div>🎂 {m.age} ans</div>}
                </div>
                {m.engagement_types?.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1 text-xs">
                    {m.engagement_types.map((e) => <span key={e} className="chip bg-cef-paper text-[10px] py-0.5">#{e}</span>)}
                  </div>
                )}
                {m.message && <p className="mt-2 text-xs italic text-neutral-600">« {m.message} »</p>}
                <div className="mt-1 text-xs text-neutral-400">{m.created_at ? new Date(m.created_at).toLocaleDateString("fr-BE") : ""}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => sendMail(m)} disabled={!m.email} className="neo-btn bg-cef-yellow text-sm py-2 disabled:opacity-40" data-testid={`mail-member-${m.id}`}>
                  <Mail className="w-4 h-4" /> Email
                </button>
                <button onClick={() => del(m)} className="neo-btn bg-cef-pink text-white text-sm py-2" data-testid={`del-member-${m.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
